import { HttpClient, HttpEvent } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { handleErrorLocallyHttpOptions } from '@app/shared/interceptors/handle-error-locally.context';
import { FileMetadata } from '@app/shared/types/models/files/file-metadata';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { FileDTO } from '../dtos/file/file.dto';
import { FolderFileDTO } from '../dtos/folders/folder.dto';

/**
 * FileHttpService - HTTP API layer for file operations.
 *
 * Handles HTTP communication with the files API endpoints.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Used by {@link FileDataService} to perform API calls.
 *
 * Architecture:
 * - {@link FileActionsService}: User actions, mutations, side effects
 * - {@link FileDataService}: Data access, caching, store management
 * - {@link FileHttpService}: HTTP API calls
 */
@Injectable({ providedIn: 'root' })
export class FileHttpService {
  private readonly httpClient = inject(HttpClient);

  public uploadFiles(url: string, formData: FormData): Observable<HttpEvent<FileDTO[]>> {
    return this.httpClient.post<FileDTO[]>(url, formData, { reportUploadProgress: true, observe: 'events' });
  }

  public uploadFolderFiles(folderId: string, formData: FormData): Observable<HttpEvent<FolderFileDTO[]>> {
    return this.httpClient.post<FolderFileDTO[]>(
      `${environment.APIV3EndPoint}folders/${encodeURIComponent(folderId)}/files`,
      formData,
      { reportUploadProgress: true, observe: 'events' }
    );
  }

  public renameAttachment(fileId: string, name: string): Observable<FolderFileDTO> {
    return this.httpClient.put<FolderFileDTO>(
      `${environment.APIV3EndPoint}file-attachments/${encodeURIComponent(fileId)}/rename`,
      JSON.stringify(name),
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  public moveAttachment(fileId: string, targetFolderId: string): Observable<FolderFileDTO> {
    return this.httpClient.put<FolderFileDTO>(
      `${environment.APIV3EndPoint}file-attachments/${encodeURIComponent(fileId)}/move`,
      { targetFolderId }
    );
  }

  public deleteAttachment(fileId: string): Observable<void> {
    return this.httpClient.delete<void>(`${environment.APIV3EndPoint}file-attachments/${encodeURIComponent(fileId)}`);
  }

  public downloadFile(companyId: string, fileShortId: string): Observable<Blob> {
    return this.httpClient.get(`${environment.APIEndPoint}companies/${companyId}/files/${fileShortId}`, {
      responseType: 'blob',
      ...handleErrorLocallyHttpOptions(true)
    });
  }

  public updateFile(companyId: string, fileId: string, metadata: FileMetadata): Observable<FileDTO> {
    return this.httpClient.put<FileDTO>(`${environment.APIEndPoint}companies/${companyId}/files/${fileId}`, metadata);
  }
}
